import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, X } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Year-end (1099) per-driver totals + 1099-NEC generation & one-click delivery. */
export default function Annual1099Summary() {
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState(nowYear);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [showPayer, setShowPayer] = useState(false);
  const [w9Driver, setW9Driver] = useState(null);   // {driver_id, driver_name} → manual W-9 modal

  const load = useCallback(() => {
    api.get("/payouts/1099", { params: { year } })
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Could not load 1099 summary"));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const generate = async (driverIds) => {
    setBusy("gen");
    try {
      const r = await api.post("/payouts/1099/generate", { year, driver_ids: driverIds || null });
      const g = r.data.generated.length, s = r.data.skipped.length;
      toast.success(`${g} form${g === 1 ? "" : "s"} generated${s ? ` · ${s} skipped` : ""}`);
      if (s && !driverIds) toast.info(r.data.skipped.map(x => x.reason).slice(0, 3).join(" · "));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(""); }
  };

  const send = async (driverIds) => {
    setBusy("send");
    try {
      const r = await api.post("/payouts/1099/send", { year, driver_ids: driverIds || null });
      const ok = r.data.sent.length, bad = r.data.failed.length;
      toast.success(`Sent to ${ok} driver${ok === 1 ? "" : "s"}${bad ? ` · ${bad} failed` : ""}`);
      if (bad) toast.error(r.data.failed.map(f => f.reason).slice(0, 3).join(" · "));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(""); }
  };

  const exportCsv = () => {
    if (!data) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Driver", "Year", "Delux Pay (paid)", "External Pay (paid)", "Platforms", "Total Paid (1099)", "Still Unpaid"].join(",")];
    data.drivers.forEach(d => {
      const plats = Object.entries(d.by_platform).map(([k, v]) => `${k}: $${v.toFixed(2)}`).join("; ");
      lines.push([esc(d.driver_name), data.year, d.delux_paid.toFixed(2), d.external_paid.toFixed(2),
        esc(plats), d.total_paid.toFixed(2), d.unpaid_total.toFixed(2)].join(","));
    });
    lines.push([esc("GRAND TOTAL"), data.year, "", "", "", data.grand_paid.toFixed(2), data.grand_unpaid.toFixed(2)].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `driver-1099-summary-${data.year}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const years = [nowYear, nowYear - 1, nowYear - 2];
  const eligible = data?.drivers.filter(d => d.w9_on_file && d.total_paid > 0) || [];
  const generated = data?.drivers.filter(d => d.form_file_id) || [];

  return (
    <div data-testid="annual-1099-summary" className="mt-12 mb-8">
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <h3 className="font-serif text-xl">Year-end totals (1099)</h3>
        <div className="flex gap-1">
          {years.map(y => (
            <button key={y} data-testid={`y1099-${y}`} onClick={() => setYear(y)}
              className={`px-2.5 py-1 text-[10px] tracking-[0.15em] border ${
                year === y ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#A1A1AA]"
              }`}>{y}</button>
          ))}
        </div>
        {data && (
          <span className="text-[11px] text-[#A1A1AA]">
            total paid <span className="text-[#D4AF37] font-serif text-sm">${data.grand_paid.toFixed(2)}</span>
            {data.grand_unpaid > 0 && <span className="ml-2 text-red-300">${data.grand_unpaid.toFixed(2)} still unpaid</span>}
          </span>
        )}
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <button data-testid="y1099-payer-info" onClick={() => setShowPayer(true)}
            className="btn-ghost !py-1.5 !px-3 text-[10px]">Payer info</button>
          <button data-testid="y1099-generate-all" onClick={() => generate(null)} disabled={!!busy || !eligible.length}
            className="btn-ghost !py-1.5 !px-3 text-[10px] disabled:opacity-40">
            {busy === "gen" ? "Generating…" : `Generate 1099s (${eligible.length})`}
          </button>
          <button data-testid="y1099-send-all" onClick={() => send(null)} disabled={!!busy || !generated.length}
            className="btn-gold !py-1.5 !px-3 text-[10px] disabled:opacity-40">
            {busy === "send" ? "Sending…" : "Send all 1099s"}
          </button>
          <button data-testid="y1099-export" onClick={exportCsv} disabled={!data}
            className="btn-ghost !py-1.5 !px-3 text-[10px]"><Download size={12}/>Summary CSV</button>
          <a data-testid="y1099-filing-export"
            href={generated.length ? `${API_BASE}/payouts/1099/filing-export.csv?year=${year}` : undefined}
            onClick={e => { if (!generated.length) { e.preventDefault(); toast.info("Generate 1099s first"); } }}
            className="btn-ghost !py-1.5 !px-3 text-[10px]"><Download size={12}/>Filing CSV (full TINs)</a>
        </div>
      </div>

      {data && data.drivers.length === 0 && (
        <div className="border border-white/10 bg-[#0A0A0A] p-6 text-center text-[#71717A] text-sm" data-testid="y1099-empty">
          No driver payments recorded in {data.year}.
        </div>
      )}

      {data && data.drivers.length > 0 && (
        <div className="border border-white/10 bg-[#0A0A0A] overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-black/40 text-[9px] uppercase tracking-[0.18em] text-[#71717A]">
              <tr>
                <th className="text-left px-4 py-2">Driver</th>
                <th className="text-left px-2">W-9</th>
                <th className="text-right px-2">Delux pay</th>
                <th className="text-right px-2">External pay</th>
                <th className="text-left px-4 pl-6">Platforms</th>
                <th className="text-right px-2">Total paid (1099)</th>
                <th className="text-right px-2">Still unpaid</th>
                <th className="text-left px-4">1099-NEC</th>
              </tr>
            </thead>
            <tbody>
              {data.drivers.map(d => (
                <tr key={d.driver_id} className="border-t border-white/5" data-testid={`y1099-row-${d.driver_id}`}>
                  <td className="px-4 py-2.5 text-[#F5F2EB]">{d.driver_name}</td>
                  <td className="px-2" data-testid={`w9-status-${d.driver_id}`}>
                    {d.w9_on_file
                      ? <span className="text-[10px] text-emerald-300" title={`${d.w9_legal_name || ""} · TIN ***${d.w9_tin_last4 || ""}`}>✓ ***{d.w9_tin_last4}</span>
                      : <button data-testid={`w9-fill-${d.driver_id}`}
                          onClick={() => setW9Driver({ driver_id: d.driver_id, driver_name: d.driver_name })}
                          className="text-[10px] text-amber-300 underline underline-offset-2 hover:text-amber-200"
                          title="No W-9 on file — enter it manually or send the onboarding packet from the driver profile">
                          ⚠ Fill W-9
                        </button>}
                  </td>
                  <td className="px-2 text-right font-mono text-[#A1A1AA]">${d.delux_paid.toFixed(2)}<span className="text-[9px] text-[#71717A] ml-1">({d.delux_trips})</span></td>
                  <td className="px-2 text-right font-mono text-[#A1A1AA]">${d.external_paid.toFixed(2)}<span className="text-[9px] text-[#71717A] ml-1">({d.external_trips})</span></td>
                  <td className="px-4 pl-6 text-[10px] text-[#71717A]">
                    {Object.entries(d.by_platform).map(([k, v]) => `${k} $${v.toFixed(2)}`).join(" · ") || "—"}
                  </td>
                  <td className="px-2 text-right font-mono text-[#D4AF37] font-bold">
                    ${d.total_paid.toFixed(2)}
                    {d.total_paid > 0 && d.total_paid < 600 && <span className="block text-[8px] text-[#71717A] font-sans">under $600 — 1099 optional</span>}
                  </td>
                  <td className="px-2 text-right font-mono">{d.unpaid_total > 0 ? <span className="text-red-300">${d.unpaid_total.toFixed(2)}</span> : <span className="text-[#71717A]">—</span>}</td>
                  <td className="px-4 py-2" data-testid={`form1099-cell-${d.driver_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {d.form_file_id ? (
                        <>
                          <a href={`${API_BASE}/files/${d.form_file_id}/content`} target="_blank" rel="noreferrer"
                            data-testid={`form1099-view-${d.driver_id}`}
                            className="text-[10px] text-[#D4AF37] underline underline-offset-2">PDF ${Number(d.form_amount || 0).toFixed(0)}</a>
                          <button data-testid={`form1099-send-${d.driver_id}`} onClick={() => send([d.driver_id])} disabled={!!busy}
                            className="text-[10px] text-[#A1A1AA] hover:text-white underline underline-offset-2 disabled:opacity-40">
                            {d.form_sent_at ? "Re-send" : "Send"}
                          </button>
                          {d.form_sent_at && (
                            <span className="text-[9px] text-emerald-300" data-testid={`form1099-sent-${d.driver_id}`}>
                              ✓ sent {new Date(d.form_sent_at).toLocaleDateString()}
                            </span>
                          )}
                        </>
                      ) : d.w9_on_file && d.total_paid > 0 ? (
                        <button data-testid={`form1099-generate-${d.driver_id}`} onClick={() => generate([d.driver_id])} disabled={!!busy}
                          className="text-[10px] text-[#A1A1AA] hover:text-[#D4AF37] underline underline-offset-2 disabled:opacity-40">Generate</button>
                      ) : (
                        <span className="text-[10px] text-[#71717A]">{d.total_paid > 0 ? "needs W-9" : "—"}</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPayer && <PayerInfoModal onClose={() => setShowPayer(false)}/>}
      {w9Driver && (
        <ManualW9Modal driver={w9Driver} onClose={() => setW9Driver(null)}
          onSaved={() => { setW9Driver(null); load(); }}/>
      )}
    </div>
  );
}

const inCls = "w-full bg-black/40 border border-white/10 px-3 py-2 text-xs text-[#F5F2EB] focus:outline-none focus:border-[#D4AF37]/60";
const lbCls = "block text-[9px] uppercase tracking-[0.2em] text-[#71717A] mb-1";

function PayerInfoModal({ onClose }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/company-tax-info").then(r => setF(r.data)).catch(() => setF({}));
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/company-tax-info", f);
      toast.success("Payer info saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  if (!f) return null;
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="payer-info-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative w-full max-w-md bg-[#0A0A0A] border border-white/10 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-serif text-lg text-[#D4AF37]">Payer info (goes on every 1099)</h3>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <div><label className={lbCls}>Company legal name</label>
            <input data-testid="payer-name" className={inCls} value={f.name || ""} onChange={e => upd("name", e.target.value)}/></div>
          <div><label className={lbCls}>Street address</label>
            <input data-testid="payer-address" className={inCls} value={f.address || ""} onChange={e => upd("address", e.target.value)}/></div>
          <div><label className={lbCls}>City, State ZIP</label>
            <input data-testid="payer-csz" className={inCls} value={f.city_state_zip || ""} onChange={e => upd("city_state_zip", e.target.value)}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbCls}>EIN (payer TIN)</label>
              <input data-testid="payer-ein" className={inCls} placeholder="12-3456789" value={f.ein || ""} onChange={e => upd("ein", e.target.value)}/></div>
            <div><label className={lbCls}>Phone</label>
              <input data-testid="payer-phone" className={inCls} value={f.phone || ""} onChange={e => upd("phone", e.target.value)}/></div>
          </div>
        </div>
        <button data-testid="payer-save" onClick={save} disabled={busy}
          className="btn-gold w-full !py-2.5 text-[11px] mt-5 disabled:opacity-50">{busy ? "Saving…" : "Save payer info"}</button>
      </div>
    </div>
  );
}

const TAX_CLASSES = [
  ["individual", "Individual"], ["sole_proprietor", "Sole proprietor / single-member LLC"],
  ["llc", "LLC"], ["s_corp", "S corporation"], ["c_corp", "C corporation"],
  ["partnership", "Partnership"], ["trust_estate", "Trust / estate"], ["other", "Other"],
];

function ManualW9Modal({ driver, onClose, onSaved }) {
  const [f, setF] = useState({
    legal_name: driver.driver_name || "", business_name: "", tax_classification: "individual",
    address: "", city: "", state: "WI", zip: "", tin_type: "ssn", tin: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/drivers/${driver.driver_id}/w9/manual`, f);
      toast.success(`W-9 recorded for ${driver.driver_name}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save W-9");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="manual-w9-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative w-full max-w-lg bg-[#0A0A0A] border border-white/10 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-1">
          <h3 className="font-serif text-lg text-[#D4AF37]">Manual W-9 — {driver.driver_name}</h3>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>
        <div className="text-[10px] text-[#71717A] mb-4">
          For drivers who gave you a paper W-9 or details over the phone. SSN/EIN is stored encrypted; only the last 4 show anywhere.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className={lbCls}>Legal name (as on tax return) *</label>
            <input data-testid="mw9-legal-name" className={inCls} value={f.legal_name} onChange={e => upd("legal_name", e.target.value)}/></div>
          <div className="col-span-2"><label className={lbCls}>Business name (if different)</label>
            <input data-testid="mw9-business-name" className={inCls} value={f.business_name} onChange={e => upd("business_name", e.target.value)}/></div>
          <div><label className={lbCls}>Tax classification *</label>
            <select data-testid="mw9-tax-class" className={inCls} value={f.tax_classification} onChange={e => upd("tax_classification", e.target.value)}>
              {TAX_CLASSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className={lbCls}>ID type *</label>
              <select data-testid="mw9-tin-type" className={inCls} value={f.tin_type} onChange={e => upd("tin_type", e.target.value)}>
                <option value="ssn">SSN</option><option value="ein">EIN</option>
              </select></div>
            <div><label className={lbCls}>{f.tin_type === "ein" ? "EIN" : "SSN"} *</label>
              <input data-testid="mw9-tin" className={inCls} inputMode="numeric" value={f.tin} onChange={e => upd("tin", e.target.value)}/></div>
          </div>
          <div className="col-span-2"><label className={lbCls}>Street address *</label>
            <input data-testid="mw9-address" className={inCls} value={f.address} onChange={e => upd("address", e.target.value)}/></div>
          <div><label className={lbCls}>City *</label>
            <input data-testid="mw9-city" className={inCls} value={f.city} onChange={e => upd("city", e.target.value)}/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className={lbCls}>State *</label>
              <input data-testid="mw9-state" className={inCls} maxLength={2} value={f.state} onChange={e => upd("state", e.target.value.toUpperCase())}/></div>
            <div><label className={lbCls}>ZIP *</label>
              <input data-testid="mw9-zip" className={inCls} inputMode="numeric" value={f.zip} onChange={e => upd("zip", e.target.value)}/></div>
          </div>
        </div>
        <button data-testid="mw9-save" onClick={save} disabled={busy}
          className="btn-gold w-full !py-2.5 text-[11px] mt-5 disabled:opacity-50">{busy ? "Saving…" : "Save W-9"}</button>
      </div>
    </div>
  );
}
